/**
 * Modal Base
 *
 * Reusable modal dialog component (base building block).
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material/styles';

import type { ReactNode } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';

export interface ModalAction {
  label: string;
  onClick: () => void | Promise<void>;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  disabled?: boolean;
  loading?: boolean;
  startIcon?: ReactNode;
  autoFocus?: boolean;
}

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  actions?: ModalAction[];
  actionsAlign?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  actionsStartContent?: ReactNode;
  showCloseIcon?: boolean;
  icon?: SvgIconComponent | undefined;
  iconColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  loading?: boolean;
  dividers?: boolean;
}

export function ModalBase({
  open,
  onClose,
  title,
  children,
  actions,
  actionsAlign = 'flex-end',
  actionsStartContent,
  showCloseIcon = true,
  icon: Icon,
  iconColor = 'primary',
  maxWidth = 'sm',
  fullWidth = true,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  loading = false,
  dividers = true,
}: ModalProps) {
  const theme = useTheme();

  const handleClose = (_event: unknown, reason?: string) => {
    if (disableBackdropClick && reason === 'backdropClick') return;
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: dividers ? 2 : 1,
          pr: showCloseIcon ? 6 : 3,
        }}
      >
        {Icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette[iconColor].main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon color={iconColor} />
          </Box>
        )}
        <Box sx={{ flex: 1 }}>{title}</Box>
        {showCloseIcon && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            disabled={loading}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers={dividers} sx={{ position: 'relative' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          children
        )}
      </DialogContent>

      {(actions && actions.length > 0) || actionsStartContent ? (
        <DialogActions sx={{ px: 3, py: 2.5, justifyContent: actionsStartContent ? 'space-between' : actionsAlign }}>
          {actionsStartContent ?? <span />}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {actions?.map((action, index) => (
              <Button
                key={index}
                onClick={action.onClick}
                variant={action.variant || 'text'}
                color={action.color || 'primary'}
                disabled={action.disabled || loading}
                autoFocus={action.autoFocus}
                startIcon={
                  action.loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    action.startIcon
                  )
                }
                sx={{ borderRadius: 1.5 }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}


